import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "react-use";

interface LoincSearchParams {
  searchText?: string;
}

export interface LoincSearchResult {
  code: string;
  display: string;
}

interface LoincSearchResponse {
  loincResults: LoincRecord[];
}

interface LoincRecord {
  loincNum: string;
  displayName?: string;
  status: string;
  mapsTo?: string;
  mapsToActive?: boolean;
  classType: number;
  className?: string;
  short_name?: string;
  orderObs?: string;
  longCommonName: string;
  definitionDescription?: string;
  commonTestRank: number;
  commonOrderRank: number;
  classAbbreviation: string;
}

export const useLoincSearch = ({ searchText }: LoincSearchParams) => {
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  useDebounce(
    () => {
      setDebouncedSearchText(searchText);
    },
    500,
    [searchText],
  );
  return useQuery<LoincSearchResponse, void, LoincSearchResult[]>({
    queryFn: async () => {
      const response = await axiosInstance.get("/ontology/loinc/search", {
        params: {
          searchText: debouncedSearchText,
          classType: 1, // Laboratory
        },
      });

      return response.data;
    },
    select: (data) => {
      const loincCodes: LoincSearchResult[] = [];
      for (const record of data.loincResults) {
        loincCodes.push({
          code: record.loincNum,
          display:
            record.displayName || record.short_name || record.longCommonName,
        });
      }
      return loincCodes;
    },
    queryKey: ["medical-coding", "loinc", "search", debouncedSearchText],
    enabled: Boolean(debouncedSearchText && debouncedSearchText.length >= 2),
    staleTime: Number.POSITIVE_INFINITY,
  });
};
